import { AmenityStatusBadge } from '@/components/amenities/AmenityStatusBadge';
import { StateDropdown } from '@/components/dropdowns';
import { BusinessHoursDisplay } from '@/components/shared/BusinessHoursDisplay';
import type { CrudConfig } from '@startupquickstart/frontend';
import { Link } from 'react-router-dom';

// Define type based on our Prisma schema
export interface Amenity {
  id: string;
  hotelId: string;
  name: string;
  description?: string;
  featuredImageId?: string;
  amenityTypeId: string;
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phoneNumber?: string;
  email?: string;
  website?: string;
  longitude?: number;
  latitude?: number;
  status?: 'published' | 'draft' | 'needs_review';
  hours?: Record<string, unknown>;
  features?: Record<string, unknown>;
  floor?: string;
  usesHotelAddress?: boolean;
  capacity?: number;
  createdAt: string;
  updatedAt: string;
  // Relations for display
  amenityType?: {
    id: string;
    name: string;
  };
  hotel?: {
    id: string;
    name: string;
    streetAddress?: string;
    streetAddress2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  featuredImage?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
}

/**
 * Amenity CRUD configuration
 */
export const amenityCrudConfig: CrudConfig<Amenity> = {
  entityName: 'Amenity',
  entityNamePlural: 'Amenities',

  endpoints: {
    list: '/amenities',
    single: (id: string) => `/amenities/${id}`,
    create: '/amenities',
    update: (id: string) => `/amenities/${id}`,
    delete: (id: string) => `/amenities/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter amenity name',
      validation: {
        min: 2,
        max: 200,
      },
    },
    {
      key: 'amenityType',
      label: 'Amenity Type',
      type: 'text',
      required: false,
      placeholder: 'Amenity Type',
      format: (value: { id: string; name: string } | undefined) => {
        return (
          <Link className="text-primary" to={`/amenity-types/${value?.id}`}>
            {value?.name || 'N/A'}
          </Link>
        );
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
      validation: {
        max: 2000,
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: false,
      defaultValue: 'published',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
        { label: 'Needs Review', value: 'needs_review' },
      ],
      format: (value: 'published' | 'draft' | 'needs_review') => {
        if (!value) return null;
        return <AmenityStatusBadge status={value} />;
      },
    },
    {
      key: 'featuredImage',
      label: 'Featured Image',
      type: 'image',
      required: false,
      placeholder: 'Featured Image ID',
      format: (value: Amenity['featuredImage']) => {
        if (!value) return null;
        const imageUrl = value.url || `/api/v1/media/${value.id}`;
        return <img src={imageUrl} alt="Featured" className="w-full max-w-md rounded-lg" />;
      },
    },
    {
      key: 'amenityTypeId',
      label: 'Amenity Type',
      type: 'select',
      required: true,
      placeholder: 'Select Amenity Type',
      options: [],
    },
    {
      key: 'hotelId',
      label: 'Hotel',
      type: 'select',
      required: true,
      placeholder: 'Select Hotel',
      options: [],
    },
    // Contact Information
    {
      key: 'phoneNumber',
      label: 'Phone Number',
      type: 'text',
      required: false,
      placeholder: 'Phone Number',
    },
    {
      key: 'email',
      label: 'Email',
      type: 'email',
      required: false,
      placeholder: 'Email Address',
    },
    {
      key: 'website',
      label: 'Website',
      type: 'text',
      required: false,
      placeholder: 'Website URL',
    },
    // Address Information
    {
      key: 'usesHotelAddress',
      label: 'Uses Hotel Address',
      type: 'boolean',
      required: false,
    },
    {
      key: 'streetAddress',
      label: 'Address',
      type: 'text',
      required: false,
      placeholder: 'Street Address',
      format: (value: string, item?: Amenity) => {
        // Show hotel address if usesHotelAddress is true
        if (item?.usesHotelAddress && item?.hotel) {
          const addressLines = [
            item.hotel.streetAddress,
            item.hotel.streetAddress2,
            [item.hotel.city, item.hotel.state].filter(Boolean).join(', '),
            item.hotel.postalCode,
            item.hotel.country,
          ].filter(Boolean);

          if (addressLines.length === 0) return null;

          return <div className="whitespace-pre-line text-sm">{addressLines.join('\n')}</div>;
        }

        // Show amenity's own address
        if (!value) return null;

        const addressLines = [
          value,
          item?.streetAddress2,
          [item?.city, item?.state].filter(Boolean).join(', '),
          item?.postalCode,
          item?.country,
        ].filter(Boolean);

        return <div className="whitespace-pre-line text-sm">{addressLines.join('\n')}</div>;
      },
    },
    {
      key: 'streetAddress2',
      label: 'Address Line 2',
      type: 'text',
      required: false,
      placeholder: 'Apartment, suite, etc.',
    },
    {
      key: 'city',
      label: 'City',
      type: 'text',
      required: false,
      placeholder: 'City',
    },
    {
      key: 'state',
      label: 'State',
      type: 'custom',
      required: false,
      placeholder: 'Select State',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => (
        <StateDropdown value={value} onChange={onChange} placeholder={field.placeholder} />
      ),
    },
    {
      key: 'postalCode',
      label: 'Postal Code',
      type: 'text',
      required: false,
      placeholder: 'Postal Code',
    },
    {
      key: 'country',
      label: 'Country',
      type: 'text',
      required: false,
      placeholder: 'Country',
    },
    // Location Details
    {
      key: 'floor',
      label: 'Floor',
      type: 'text',
      required: false,
      placeholder: 'Floor',
    },
    {
      key: 'capacity',
      label: 'Capacity',
      type: 'number',
      required: false,
      placeholder: 'Capacity',
    },
    {
      key: 'latitude',
      label: 'Latitude',
      type: 'number',
      required: false,
      placeholder: 'Latitude',
      readonly: true,
      format: (value: number | null) => (value != null ? value : '-'),
    },
    {
      key: 'longitude',
      label: 'Longitude',
      type: 'number',
      required: false,
      placeholder: 'Longitude',
      readonly: true,
      format: (value: number | null) => (value != null ? value : '-'),
    },
    {
      key: 'hours',
      label: 'Business Hours',
      type: 'json',
      required: false,
      format: (value: Record<string, unknown>) => <BusinessHoursDisplay value={value} />,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['name', 'amenityType', 'status', 'createdAt', 'updatedAt'],
    searchableFields: ['name'],
    sortableFields: ['name', 'status', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'name',
      direction: 'asc',
    },
    itemsPerPage: 10,
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Published', value: 'published' },
          { label: 'Draft', value: 'draft' },
          { label: 'Needs Review', value: 'needs_review' },
        ],
      },
    ],
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core amenity details',
        fields: ['name', 'status', 'featuredImageId', 'amenityType', 'description'],
        defaultExpanded: true,
      },
      {
        title: 'Contact Information',
        description: 'Contact details for the amenity',
        fields: ['phoneNumber', 'email', 'website'],
        defaultExpanded: true,
      },
      {
        title: 'Location',
        description: 'Address and location details',
        fields: ['usesHotelAddress', 'floor', 'capacity', 'streetAddress'],
        defaultExpanded: true,
      },
      {
        title: 'Hours',
        description: 'Business hours',
        fields: ['hours'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['latitude', 'longitude', 'createdAt', 'updatedAt'],
        defaultExpanded: true,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'General Information about the Amenity',
        fields: ['featuredImageId', 'name', 'amenityTypeId', 'hotelId', 'status', 'description'],
        columns: 1,
      },
      {
        title: 'Contact',
        description: 'Contact Information for the Amenity',
        fields: ['phoneNumber', 'email', 'website'],
        columns: 2,
      },
      {
        title: 'Location',
        description: 'Location Information for the Amenity',
        fields: [
          'usesHotelAddress',
          'floor',
          'capacity',
          'streetAddress',
          'streetAddress2',
          'city',
          'state',
          'postalCode',
          'country',
        ],
        columns: 2,
      },
      {
        title: 'Hours',
        description: 'Business Hours',
        fields: ['hours'],
        columns: 1,
      },
    ],
    validation: {
      name: {
        required: true,
        min: 2,
        max: 200,
      },
      amenityTypeId: {
        required: true,
      },
      hotelId: {
        required: true,
      },
    },
  },
};
